import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Terminal, Globe, Search } from "lucide-react";
import usePageview from "@/lib/usePageview";

// Phase 25.14b — DARA Pulse Wall.
// Public, scrollable feed of short "screenshot-of-DARA-Pulse" cards.
// Designed to be social-proof-able from a phone screenshot.
//   /wall                  — full grid
//   /wall?industry=barber  — filtered

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACCENT = "#FFB000";

export default function PulseWall() {
  usePageview("/wall");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    fetch(`${API}/wall/pulses?limit=200`)
      .then(r => r.ok ? r.json() : { items: [] })
      .then(d => setItems(d.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const industries = useMemo(() => {
    const set = new Set();
    items.forEach(i => { if (i.industry) set.add(i.industry); });
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return items;
    return items.filter(i =>
      [i.operator_name, i.industry, i.city, i.pulse_text].some(f => (f || "").toLowerCase().includes(q))
    );
  }, [items, filter]);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="pulse-wall-page">
      <header className="border-b border-zinc-900 sticky top-0 z-10 bg-black/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <Terminal className="w-5 h-5" style={{ color: ACCENT }} />
            <span className="font-heading font-black tracking-tight text-lg">DARA</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/case-studies" className="font-mono text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white px-3 py-2 hidden sm:inline">
              CASE STUDIES
            </Link>
            <Link to="/login" className="font-mono text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white px-3 py-2">
              LOGIN
            </Link>
          </div>
        </div>
      </header>

      <section className="border-b border-zinc-900 py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-4" style={{ color: ACCENT }}>
            // OPERATORS SPEAKING — UNFILTERED
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            What DARA caught this week.
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto mt-5 leading-relaxed">
            Real operators. Real moments DARA flagged on their dashboard.
            Each card is one shop, one signal, one number — the kind of thing
            you&apos;d only notice if you stared at your POS data for hours every day.
          </p>
        </div>
      </section>

      {/* Filter strip */}
      {items.length > 0 && (
        <div className="border-b border-zinc-900 sticky top-[57px] z-10 bg-black/95 backdrop-blur">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[180px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-600" />
              <input
                data-testid="wall-search"
                placeholder="Search shops, industries, cities…"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-900 pl-9 pr-3 py-2 font-mono text-[11px] text-white placeholder:text-zinc-700 focus:outline-none focus:border-zinc-700"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setFilter("")}
                data-testid="wall-filter-all"
                className={`font-mono text-[10px] uppercase tracking-widest px-2.5 py-1.5 border ${!filter ? "border-zinc-700 text-white" : "border-zinc-900 text-zinc-500 hover:border-zinc-700"}`}
              >
                ALL · {items.length}
              </button>
              {industries.map(ind => (
                <button
                  key={ind}
                  onClick={() => setFilter(ind)}
                  data-testid={`wall-filter-${ind.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`font-mono text-[10px] uppercase tracking-widest px-2.5 py-1.5 border ${filter.toLowerCase() === ind.toLowerCase() ? "border-zinc-700 text-white" : "border-zinc-900 text-zinc-500 hover:border-zinc-700"}`}
                >
                  {ind}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <section className="py-10 sm:py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {loading && (
            <div className="text-center font-mono text-[11px] uppercase tracking-widest text-zinc-600 py-12">
              LOADING…
            </div>
          )}
          {!loading && filtered.length === 0 && items.length === 0 && (
            <div className="border border-zinc-900 bg-zinc-950/40 p-10 text-center max-w-2xl mx-auto">
              <Globe className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <h3 className="font-heading font-bold text-xl">The wall&apos;s still being built.</h3>
              <p className="text-sm text-zinc-400 mt-3 max-w-md mx-auto leading-relaxed">
                We post here once we&apos;ve sat with an operator and watched DARA flag
                something worth flagging. Real signals only — no testimonials manufactured
                from thin air.
              </p>
              <Link
                to="/case-studies"
                className="inline-flex items-center gap-2 mt-5 border border-zinc-700 hover:border-white font-mono text-[11px] uppercase tracking-widest px-4 py-2.5 text-white"
              >
                Read the long-form case studies →
              </Link>
            </div>
          )}
          {!loading && filtered.length === 0 && items.length > 0 && (
            <div className="text-center font-mono text-[11px] uppercase tracking-widest text-zinc-600 py-8">
              NO PULSES MATCH &quot;{filter}&quot;
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map(p => (
                <PulseCard key={p.id} pulse={p} />
              ))}
            </div>
          )}
        </div>
      </section>

      <footer className="border-t border-zinc-900 py-10 mt-6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between flex-wrap gap-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-700">
            © DARA · Real signals from real operators.
          </div>
          <Link to="/" className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white">
            ← BACK TO DARA
          </Link>
        </div>
      </footer>
    </div>
  );
}

function PulseCard({ pulse }) {
  const meta = [pulse.industry, pulse.city].filter(Boolean).join(" · ");
  const inner = (
    <div
      data-testid={`pulse-card-${pulse.id}`}
      className="h-full border border-zinc-900 hover:border-zinc-700 bg-zinc-950/40 p-5 flex flex-col gap-3 transition-colors"
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="min-w-0">
          <div className="font-heading font-bold text-base text-white truncate">
            {pulse.operator_name}
          </div>
          {meta && (
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
              {meta}
            </div>
          )}
        </div>
        {pulse.money_saved_label && (
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#00E676] flex-shrink-0">
            {pulse.money_saved_label}
          </div>
        )}
      </div>
      <p className="text-sm text-zinc-100 leading-relaxed flex-1">
        &ldquo;{pulse.pulse_text}&rdquo;
      </p>
      <div className="flex items-center justify-between pt-1 border-t border-zinc-900">
        <span className="font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: ACCENT }}>
          DARA PULSE
        </span>
        {pulse.case_study_slug && (
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 group-hover:text-white">
            Read story →
          </span>
        )}
      </div>
    </div>
  );

  if (pulse.case_study_slug) {
    return (
      <Link to={`/case-studies/${pulse.case_study_slug}`} className="group block">
        {inner}
      </Link>
    );
  }
  return <div className="block">{inner}</div>;
}
